import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
// SOURCE_LABELS no longer used here - history moved to /panel/historia
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, Save, BadgeCheck, ShieldAlert, Send, X, MapPin, Phone, Mail, Globe, FileText, Zap, ExternalLink } from "lucide-react";
import { PanelBadge } from "@/components/panel/PanelBadge";
import { format } from "date-fns";
import { pl } from "date-fns/locale";
import { toast } from "sonner";
import { dbErrorMessage } from "@/lib/dbErrorMessage";
import { LocationPicker } from "@/components/LocationPicker";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { useReverseGeocode } from "@/hooks/useReverseGeocode";

const fetchShelter = async (slug: string) => {
  const { data, error } = await supabase
    .from("shelters")
    .select("*")
    .eq("slug", slug)
    .single();
  if (error) throw error;
  return data;
};

const IDENTITY_FIELDS = ["name", "address", "postal_code", "city", "region", "lat", "lng"] as const;

const normalize = (v: unknown) =>
  typeof v === "string" ? (v.trim() || null) : (v === undefined ? null : v);

const EditProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: shelter, isLoading } = useQuery({
    queryKey: ["panel-shelter", slug],
    enabled: !!slug,
    queryFn: () => fetchShelter(slug!),
  });

  const shelterId = shelter?.id;

  // Historia zmian została przeniesiona do osobnej zakładki /panel/historia/:slug

  const { data: pendingProfileRequest, refetch: refetchPending } = useQuery({
    queryKey: ["pending-profile-request", shelterId, user?.id],
    enabled: !!shelterId && !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shelter_updates")
        .select("id, created_at, shelter_name, city, region, address, postal_code")
        .eq("shelter_id", shelterId!)
        .eq("submitter_user_id", user!.id)
        .eq("source", "owner_profile_request")
        .eq("status", "pending")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const [identity, setIdentity] = useState<any>(null);
  const [contact, setContact] = useState<any>(null);
  const [submittingIdentity, setSubmittingIdentity] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);
  
  const [cancelling, setCancelling] = useState(false);
  const { reverseGeocode, isLoading: isReverseGeocoding } = useReverseGeocode();

  useEffect(() => {
    if (shelter) {
      setIdentity({
        name: shelter.name ?? "",
        address: shelter.address ?? "",
        postal_code: shelter.postal_code ?? "",
        city: shelter.city ?? "",
        region: shelter.region ?? "",
        lat: shelter.lat ?? null,
        lng: shelter.lng ?? null,
      });
      setContact({
        phone: shelter.phone ?? "",
        email: shelter.email ?? "",
        website: shelter.website ?? "",
        description: shelter.description ?? "",
      });
    }
  }, [shelter]);

  const identityChanged = useMemo(() => {
    if (!shelter || !identity) return false;
    return IDENTITY_FIELDS.some(
      (k) => normalize(identity[k]) !== normalize((shelter as any)[k])
    );
  }, [shelter, identity]);

  if (isLoading || !identity || !contact || !shelter) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasPending = !!pendingProfileRequest;


  const handleSubmitIdentity = async () => {
    if (!shelter || !user) return;
    if (hasPending) {
      toast.info("Masz już oczekujący wniosek - poczekaj na decyzję administratora.");
      return;
    }
    setSubmittingIdentity(true);
    try {
      const changedKeys: string[] = [];
      const originalValues: Record<string, any> = {};

      const payload: Record<string, any> = {
        shelter_id: shelter.id,
        // Pola NOT NULL - obowiązkowo wypełnione
        shelter_name: normalize(identity.name) ?? shelter.name,
        city: normalize(identity.city) ?? shelter.city,
        lat: identity.lat ?? shelter.lat,
        lng: identity.lng ?? shelter.lng,
        // Pola identyfikacyjne (mogą być NULL)
        region: normalize(identity.region),
        address: normalize(identity.address),
        postal_code: normalize(identity.postal_code),
        // Reszta - MUSI być NULL (trigger walidujący)
        phone: null,
        email: null,
        website: null,
        current_needs: null,
        urgent_needs: null,
        dog_walking_available: null,
        volunteering_available: null,
        dogs_available: null,
        cats_available: null,
        volunteer_info: null,
        description: null,
        status: "pending",
        source: "owner_profile_request",
        is_organization_request: false,
        submitter_user_id: user.id,
        submitter_email: user.email ?? null,
      };

      // Zbuduj listę zmienionych pól (whitelist dla approve)
      // Mapowanie: name → shelter_name (tak jest w shelter_updates)
      const fieldMap: Array<[string, string]> = [
        ["name", "shelter_name"],
        ["address", "address"],
        ["postal_code", "postal_code"],
        ["city", "city"],
        ["region", "region"],
        ["lat", "lat"],
        ["lng", "lng"],
      ];
      for (const [stateKey, updateKey] of fieldMap) {
        if (normalize(identity[stateKey]) !== normalize((shelter as any)[stateKey])) {
          changedKeys.push(updateKey);
          originalValues[updateKey] = (shelter as any)[stateKey] ?? null;
        }
      }

      if (changedKeys.length === 0) {
        toast.info("Nie zmieniono żadnych danych");
        setSubmittingIdentity(false);
        return;
      }

      payload.original_values = { ...originalValues, __changed_fields: changedKeys };

      const { error } = await supabase.from("shelter_updates").insert(payload as any);
      if (error) throw error;

      toast.success("Wniosek wysłany do administratora", {
        description: "Otrzymasz informację po zatwierdzeniu (zwykle 24-48h).",
      });
      refetchPending();
    } catch (err: any) {
      toast.error(dbErrorMessage(err, "Nie udało się wysłać wniosku"));
    } finally {
      setSubmittingIdentity(false);
    }
  };

  const handleCancelPending = async () => {
    if (!pendingProfileRequest) return;
    setCancelling(true);
    try {
      const { error } = await supabase
        .from("shelter_updates")
        .delete()
        .eq("id", pendingProfileRequest.id);
      if (error) throw error;
      toast.success("Wniosek anulowany");
      refetchPending();
    } catch (err: any) {
      toast.error(dbErrorMessage(err, "Nie udało się anulować"));
    } finally {
      setCancelling(false);
    }
  };

  const saveInstant = async (
    fields: Record<string, any>,
    setBusy: (b: boolean) => void,
    successLabel: string
  ) => {
    if (!shelter || !user) return;
    setBusy(true);
    try {
      const changed: Record<string, any> = {};
      const originalValues: Record<string, any> = {};
      Object.entries(fields).forEach(([key, value]) => {
        const newVal = normalize(value);
        const oldVal = normalize((shelter as any)[key]);
        if (newVal !== oldVal) {
          changed[key] = newVal;
          originalValues[key] = (shelter as any)[key] ?? null;
        }
      });

      if (Object.keys(changed).length === 0) {
        toast.info("Brak zmian do zapisu");
        setBusy(false);
        return;
      }

      const { error: updErr } = await supabase
        .from("shelters")
        .update(changed as any)
        .eq("id", shelter.id);
      if (updErr) throw updErr;

      const snap = { ...shelter, ...changed };
      const { error: histErr } = await supabase.from("shelter_updates").insert({
        shelter_id: shelter.id,
        shelter_name: snap.name,
        city: snap.city,
        region: snap.region ?? null,
        address: snap.address ?? null,
        postal_code: snap.postal_code ?? null,
        lat: snap.lat,
        lng: snap.lng,
        phone: snap.phone ?? null,
        email: snap.email ?? null,
        website: snap.website ?? null,
        description: snap.description ?? null,
        status: "approved",
        source: "owner_edit",
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        submitter_user_id: user.id,
        submitter_email: user.email ?? null,
        original_values: originalValues,
      } as any);
      if (histErr) {
        console.error("[EditProfile] shelter_updates insert failed", histErr);
        toast.warning(`${successLabel}, ale nie udało się zapisać wpisu w historii`);
      } else {
        toast.success(successLabel);
      }
      queryClient.invalidateQueries({ queryKey: ["panel-shelter", slug] });
      queryClient.invalidateQueries({ queryKey: ["owned-shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelters"] });
      queryClient.invalidateQueries({ queryKey: ["shelter-details", shelter.id] });
      queryClient.invalidateQueries({ queryKey: ["panel-history-full", shelter.id] });
    } catch (err: any) {
      toast.error(dbErrorMessage(err, "Nie udało się zapisać zmian"));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveContact = () =>
    saveInstant(
      { phone: contact.phone, email: contact.email, website: contact.website },
      setSavingContact,
      "Zapisano kontakt"
    );

  const handleSaveDescription = () =>
    saveInstant({ description: contact.description }, setSavingDescription, "Zapisano opis");

  return (
    <div className="max-w-3xl mx-auto space-y-2.5 sm:space-y-5 [&_label]:text-xs sm:[&_label]:text-sm [&_input]:h-9 [&_input]:text-sm sm:[&_input]:h-10 [&_textarea]:text-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5 min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{shelter.name}{shelter.city ? ` - ${shelter.city}` : ""}</span>
            {shelter.organization_verified && (
              <PanelBadge
                variant="verified"
                icon={BadgeCheck}
                tooltip="Profil potwierdzony przez przedstawiciela organizacji"
              >
                Zweryfikowany
              </PanelBadge>
            )}
          </div>
          <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Profil schroniska</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Zaktualizuj dane kontaktowe, adres i opis widoczny na publicznym profilu.
          </p>
        </div>
        <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground shrink-0 hidden sm:inline-flex">
          <a href={`https://gdzieschronisko.pl/schronisko/${shelter.slug}`} target="_blank" rel="noopener noreferrer">
            Zobacz publiczny profil
            <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
          </a>
        </Button>
      </div>

      {/* IDENTITY - admin approval required */}
      <Card>
        <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300">
              <ShieldAlert className="h-4 w-4" />
            </span>
            <div className="space-y-1 min-w-0">
              <CardTitle className="text-sm sm:text-base">Dane identyfikacyjne</CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                Zmiana nazwy, adresu lub miasta wymaga weryfikacji administratora. Zwykle do 24-48h.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-2.5 sm:space-y-4">
          {hasPending && (
            <div className="flex items-start justify-between gap-3 rounded-md border border-amber-300/50 bg-amber-50/60 dark:border-amber-800/40 dark:bg-amber-950/20 p-3">
              <div className="text-sm">
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  Oczekuje na weryfikację administratora
                </p>
                <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5">
                  Wysłano {format(new Date(pendingProfileRequest!.created_at), "d MMM yyyy, HH:mm", { locale: pl })}.
                  Edycja zablokowana do decyzji.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelPending}
                disabled={cancelling}
                className="shrink-0"
              >
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4 mr-1" />}
                Anuluj
              </Button>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Nazwa schroniska</Label>
            <Input
              id="name"
              value={identity.name}
              disabled={hasPending}
              onChange={(e) => setIdentity({ ...identity, name: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="city">Miasto</Label>
              <AddressAutocomplete
                id="city"
                value={identity.city}
                disabled={hasPending}
                type="city"
                placeholder="np. Warszawa"
                onChange={(value) => setIdentity((p: any) => ({ ...p, city: value }))}
                onSuggestionSelect={(s) => {
                  setIdentity((p: any) => ({
                    ...p,
                    city: s.city ?? p.city,
                    region: s.region ?? p.region,
                    postal_code: s.postalCode ?? p.postal_code,
                    lat: s.lat ?? p.lat,
                    lng: s.lng ?? p.lng,
                  }));
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postal_code">Kod pocztowy</Label>
              <Input
                id="postal_code"
                value={identity.postal_code}
                disabled={hasPending}
                placeholder="00-000"
                onChange={(e) => setIdentity({ ...identity, postal_code: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="region">Województwo</Label>
              <Input
                id="region"
                value={identity.region}
                disabled={hasPending}
                placeholder="np. mazowieckie"
                onChange={(e) => setIdentity({ ...identity, region: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Adres (ulica i numer)</Label>
            <AddressAutocomplete
              id="address"
              value={identity.address}
              disabled={hasPending}
              placeholder="np. Paluch 2"
              type="address"
              filterByCity={identity.city}
              onChange={(value) => setIdentity((p: any) => ({ ...p, address: value }))}
              onSuggestionSelect={(s) => {
                setIdentity((p: any) => ({
                  ...p,
                  address: s.address ?? p.address,
                  city: s.city ?? p.city,
                  region: s.region ?? p.region,
                  postal_code: s.postalCode ?? p.postal_code,
                  lat: s.lat ?? p.lat,
                  lng: s.lng ?? p.lng,
                }));
              }}
            />
          </div>

          {/* Mapa - lokalizacja */}
          <div className="space-y-2 pt-2">
            <Label className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              Lokalizacja na mapie
            </Label>
            <p className="text-xs text-muted-foreground">
              Kliknij na mapę lub przeciągnij pinezkę, aby poprawić lokalizację.
            </p>
            <LocationPicker
              lat={identity.lat ?? null}
              lng={identity.lng ?? null}
              onLocationSelect={(lat, lng) => {
                if (hasPending) return;
                setIdentity((p: any) => ({ ...p, lat, lng }));
              }}
              onReverseGeocode={async (lat, lng) => {
                if (hasPending) return;
                const result = await reverseGeocode(lat, lng);
                if (result) {
                  setIdentity((p: any) => ({
                    ...p,
                    city: result.city || p.city,
                    region: result.region || p.region,
                    address: result.address || p.address,
                    postal_code: result.postalCode || p.postal_code,
                  }));
                  toast.success("Adres uzupełniony z mapy");
                }
              }}
            />
            {isReverseGeocoding && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Pobieranie adresu z mapy...
              </p>
            )}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSubmitIdentity}
              disabled={submittingIdentity || hasPending || !identityChanged}
              variant="outline"
            >
              {submittingIdentity ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Wyślij do weryfikacji
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* CONTACT - instant */}
      <Card>
        <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Phone className="h-4 w-4" />
            </span>
            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-sm sm:text-base">Kontakt</CardTitle>
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                  <Zap className="h-3 w-3" />
                  Zapis natychmiastowy
                </span>
              </div>
              <CardDescription className="text-xs sm:text-sm">Te dane są publiczne na profilu schroniska.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-2.5 sm:space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Telefon</Label>
            <div className="relative">
              <Phone className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="phone"
                className="pl-9"
                placeholder="np. 22 123 45 67"
                value={contact.phone}
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                className="pl-9"
                placeholder="kontakt@schronisko.pl"
                value={contact.email}
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="website">Strona www</Label>
            <div className="relative">
              <Globe className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="website"
                type="url"
                className="pl-9"
                placeholder="https://"
                value={contact.website}
                onChange={(e) => setContact({ ...contact, website: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleSaveContact} disabled={savingContact} size="sm" className="sm:h-10 sm:px-4">
              {savingContact ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Zapisz kontakt
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* DESCRIPTION - instant */}
      <Card>
        <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
          <div className="flex items-start gap-2.5 sm:gap-3">
            <span className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <FileText className="h-4 w-4" />
            </span>
            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-sm sm:text-base">Opis schroniska</CardTitle>
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                  <Zap className="h-3 w-3" />
                  Zapis natychmiastowy
                </span>
              </div>
              <CardDescription className="text-xs sm:text-sm">Krótka historia, misja, czym wyróżnia się Wasze schronisko.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0 space-y-2.5">
          <Textarea
            rows={7}
            maxLength={3000}
            value={contact.description}
            onChange={(e) => setContact({ ...contact, description: e.target.value })}
            placeholder="O schronisku, historii, misji..."
            className="resize-y"
          />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground tabular-nums">
              {(contact.description ?? "").length} / 3000
            </span>
            <Button onClick={handleSaveDescription} disabled={savingDescription} size="sm" className="sm:h-10 sm:px-4">
              {savingDescription ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Zapisz opis
            </Button>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default EditProfile;
